/** Deterministic daily “Co-Star energy” — for fun, not professional astrology */

export type ZodiacId =
  | 'aries'
  | 'taurus'
  | 'gemini'
  | 'cancer'
  | 'leo'
  | 'virgo'
  | 'libra'
  | 'scorpio'
  | 'sagittarius'
  | 'capricorn'
  | 'aquarius'
  | 'pisces';

export interface ZodiacSign {
  id: ZodiacId;
  name: string;
  symbol: string;
  dates: string;
}

export const ZODIAC_SIGNS: ZodiacSign[] = [
  { id: 'aries', name: 'Aries', symbol: '♈', dates: 'Mar 21 – Apr 19' },
  { id: 'taurus', name: 'Taurus', symbol: '♉', dates: 'Apr 20 – May 20' },
  { id: 'gemini', name: 'Gemini', symbol: '♊', dates: 'May 21 – Jun 20' },
  { id: 'cancer', name: 'Cancer', symbol: '♋', dates: 'Jun 21 – Jul 22' },
  { id: 'leo', name: 'Leo', symbol: '♌', dates: 'Jul 23 – Aug 22' },
  { id: 'virgo', name: 'Virgo', symbol: '♍', dates: 'Aug 23 – Sep 22' },
  { id: 'libra', name: 'Libra', symbol: '♎', dates: 'Sep 23 – Oct 22' },
  { id: 'scorpio', name: 'Scorpio', symbol: '♏', dates: 'Oct 23 – Nov 21' },
  { id: 'sagittarius', name: 'Sagittarius', symbol: '♐', dates: 'Nov 22 – Dec 21' },
  { id: 'capricorn', name: 'Capricorn', symbol: '♑', dates: 'Dec 22 – Jan 19' },
  { id: 'aquarius', name: 'Aquarius', symbol: '♒', dates: 'Jan 20 – Feb 18' },
  { id: 'pisces', name: 'Pisces', symbol: '♓', dates: 'Feb 19 – Mar 20' },
];

const SIGNS_ORDER = [
  'Aries',
  'Taurus',
  'Gemini',
  'Cancer',
  'Leo',
  'Virgo',
  'Libra',
  'Scorpio',
  'Sagittarius',
  'Capricorn',
  'Aquarius',
  'Pisces',
] as const;

function degSin(d: number): number {
  return Math.sin((d * Math.PI) / 180);
}

/** Julian date (UT) */
function julianDay(d: Date): number {
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth() + 1;
  const day =
    d.getUTCDate() +
    d.getUTCHours() / 24 +
    d.getUTCMinutes() / 1440 +
    d.getUTCSeconds() / 86400;
  const a = Math.floor((14 - m) / 12);
  const yy = y + 4800 - a;
  const mm = m + 12 * a - 3;
  return (
    day +
    Math.floor((153 * mm + 2) / 5) +
    365 * yy +
    Math.floor(yy / 4) -
    Math.floor(yy / 100) +
    Math.floor(yy / 400) -
    32045
  );
}

/** Approximate moon ecliptic longitude (tropical), ~±1° typical */
export function getMoonSignLongitude(date: Date = new Date()): {
  sign: string;
  degreeInSign: number;
} {
  const jd = julianDay(date);
  const T = (jd - 2451545.0) / 36525;
  const Lp =
    ((218.3164477 + 481267.88123421 * T - 1.578e-3 * T * T) % 360) + 360;
  const D =
    ((297.8501921 + 445267.1114034 * T - 1.09e-6 * T * T) % 360) + 360;
  const M = ((357.5291092 + 35999.0502909 * T) % 360) + 360;
  const Mp =
    ((134.9633964 + 477198.8675055 * T + 3.697e-3 * T * T) % 360) + 360;
  const F =
    ((93.272095 + 483202.0175233 * T - 3.653e-3 * T * T) % 360) + 360;

  let lambda =
    Lp +
    6.288774 * degSin(Mp) +
    1.274027 * degSin(2 * D - Mp) +
    0.658314 * degSin(2 * D) +
    0.213618 * degSin(2 * Mp) -
    0.185116 * degSin(M) -
    0.114336 * degSin(2 * F);
  lambda = ((lambda % 360) + 360) % 360;
  const idx = Math.floor(lambda / 30) % 12;
  const degreeInSign = lambda % 30;
  return { sign: SIGNS_ORDER[idx], degreeInSign: Math.round(degreeInSign * 10) / 10 };
}

function hash32(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function pick<T>(arr: T[], seed: number): T {
  return arr[(seed >>> 0) % arr.length];
}

/** General “cosmic threat” / intensity 1–5 (playful daily vibe) */
export function getCosmicThreatLevel(
  date: Date,
  moonPhaseName: string
): { level: 1 | 2 | 3 | 4 | 5; label: string; blurb: string } {
  const key = date.toISOString().slice(0, 10);
  const moon = getMoonSignLongitude(date);
  const h = hash32(key + moon.sign + moonPhaseName);
  let base = 1 + (h % 5);
  if (moonPhaseName === 'Full Moon' || moonPhaseName === 'New Moon') base = Math.min(5, base + 1);
  const level = Math.min(5, Math.max(1, base)) as 1 | 2 | 3 | 4 | 5;

  const labels: Record<number, string> = {
    1: 'Soft static',
    2: 'Low-key orbits',
    3: 'Noticeable pull',
    4: 'High cosmic drama',
    5: 'Full tilt void',
  };
  const blurbs = [
    'Text someone you’ve been thinking about. Or don’t. The moon gets it.',
    'Boundaries are a love language today.',
    'Your intuition is loud — headphones optional.',
    'Retrograde who? Still you, but with plot twists.',
    'Main-character energy is available; side quests are optional.',
  ];
  return {
    level,
    label: labels[level],
    blurb: pick(blurbs, h),
  };
}

const HOROSCOPE_POOL: Record<ZodiacId, string[]> = {
  aries: [
    'Energy wants out — walk, dance, or rearrange furniture.',
    'Say the thing (kindly). The pause before sending is your superpower.',
    'A small win stacks. Celebrate before the next sprint.',
    'First move doesn’t have to be perfect — just honest.',
    'Competition with yourself only. Everyone else is background.',
  ],
  taurus: [
    'Comfort is not laziness; it’s maintenance.',
    'Something pretty or tasty is worth the detour.',
    'Slow is still forward. Soil doesn’t rush spring.',
    'Touch grass, fabric, or someone’s hand — sensory reset.',
    'Your “no” is a complete sentence.',
  ],
  gemini: [
    'Three tabs in the brain, one in the heart — pick one to close.',
    'A conversation shifts the plot. Listen for the subtext.',
    'Curiosity > certainty today.',
    'One thread at a time. Multitasking is a myth.',
    'Write it down before it evaporates.',
  ],
  cancer: [
    'Home — literal or emotional — needs a soft light on.',
    'Feel it all; you don’t have to fix it all.',
    'Nostalgia is a visitor, not a landlord.',
    'Feed yourself before you feed the group chat.',
    'Shell optional; softness mandatory.',
  ],
  leo: [
    'Visibility without performance: try that.',
    'Generosity boomerangs when you least expect it.',
    'Your warmth is not infinite — budget it.',
    'Applause from you counts double.',
    'Main character, but make it kind.',
  ],
  virgo: [
    'The list can wait; the body cannot.',
    'One imperfect action beats ten perfect plans.',
    'Edit your schedule like you edit your camera roll.',
    'Done > flawless.',
    'Someone admires your attention to detail. Let them.',
  ],
  libra: [
    'Balance isn’t 50/50 every hour; it’s the arc of the week.',
    'Aesthetic reset: one corner, one hour.',
    'Choose peace over being right (this once).',
    'Mirror work: ask what you actually want.',
    'Harmony includes your side of the scale.',
  ],
  scorpio: [
    'Intensity is a dial, not a default.',
    'Trust the slow reveal.',
    'Delete the draft. Write the real one.',
    'Depth over breadth in one relationship today.',
    'Transformation often looks like a nap first.',
  ],
  sagittarius: [
    'Wander in the smallest room — novelty is a mindset.',
    'Truth with tact lands better than truth with volume.',
    'The horizon is a suggestion.',
    'Laugh at the plot twist. Then steer.',
    'Adventure can be a new playlist route home.',
  ],
  capricorn: [
    'Rest is part of the climb.',
    'Structure supports softness today.',
    'You’re allowed to enjoy the view from this step.',
    'Ambition and rest share a calendar.',
    'Legacy includes how gently you treated yourself.',
  ],
  aquarius: [
    'Weird is a feature. Ship it.',
    'The group chat can survive without your hot take.',
    'Future you thanks present you for one boundary.',
    'Systems change when one person opts out.',
    'Innovation: try the boring solution first.',
  ],
  pisces: [
    'Dreams need anchors — one concrete step.',
    'Boundaries in water: gentle ripples, firm shore.',
    'Art, bath, or both. Non-negotiable.',
    'Merge with the music, not with the chaos.',
    'Intuition is data. Cross-check with sleep.',
  ],
};

export function getSignHoroscope(sign: ZodiacId, date: Date): string[] {
  const key = date.toISOString().slice(0, 10);
  const h = hash32(key + sign);
  const pool = HOROSCOPE_POOL[sign];
  const out: string[] = [];
  const used = new Set<number>();
  for (let i = 0; i < 3; i++) {
    let idx = (h + i * 31) % pool.length;
    let guard = 0;
    while (used.has(idx) && guard < pool.length) {
      idx = (idx + 1) % pool.length;
      guard++;
    }
    used.add(idx);
    out.push(pool[idx]);
  }
  return out;
}

export const TAROT_MAJOR = [
  'The Fool',
  'The Magician',
  'The High Priestess',
  'The Empress',
  'The Emperor',
  'The Hierophant',
  'The Lovers',
  'The Chariot',
  'Strength',
  'The Hermit',
  'Wheel of Fortune',
  'Justice',
  'The Hanged Man',
  'Death',
  'Temperance',
  'The Devil',
  'The Tower',
  'The Star',
  'The Moon',
  'The Sun',
  'Judgement',
  'The World',
] as const;

export function getMercuryVibe(date: Date): string {
  const h = hash32(date.toISOString().slice(0, 10) + 'mercury');
  const vibes = [
    'Comms feel clear. Say the thing.',
    'Double-text energy — pause, then send.',
    'Tech + travel: extra patience. Backup the doc.',
  ];
  return vibes[h % vibes.length];
}

const TAROT_DESCRIPTIONS: Record<(typeof TAROT_MAJOR)[number], string> = {
  'The Fool':
    'A fresh start and an open road. Take the first step without needing the whole map.',
  'The Magician':
    'You already have the tools. Focus intention and put skill to work.',
  'The High Priestess':
    'Quiet knowing. Pause, listen inward, and let the answer surface.',
  'The Empress':
    'Growth, care, and making things real. Nourish what you want to flourish.',
  'The Emperor':
    'Structure and stewardship. Draw a clear line and hold it.',
  'The Hierophant':
    'Shared wisdom and tradition. Learn from a guide, or become one.',
  'The Lovers':
    'A meaningful choice of alignment — values, people, or a path that fits.',
  'The Chariot':
    'Will and direction. Pick a heading and drive, even if the road is uneven.',
  Strength:
    'Steady courage. Meet intensity with patience rather than force.',
  'The Hermit':
    'A turn inward. Solitude and a small light are enough for the next step.',
  'Wheel of Fortune':
    'A turn of the cycle. Conditions shift — stay adaptable, not rigid.',
  Justice:
    'Cause and effect. Weigh honestly and own the outcome.',
  'The Hanged Man':
    'A new vantage point. Release the old frame before moving on.',
  Death:
    'An ending that clears space. Let what is finished complete.',
  Temperance:
    'Blend and pace. Mix opposites until they hold together.',
  'The Devil':
    'A pattern that binds. Name the attachment so it can loosen.',
  'The Tower':
    'A sudden break in the facade. What was unstable comes down; rebuild on truth.',
  'The Star':
    'Hope after strain. Keep a quiet faith and tend the small light.',
  'The Moon':
    'Unclear waters. Dreams and doubt both speak — wait for the outline.',
  'The Sun':
    'Clarity and warmth. What is true is easier to see and share.',
  Judgement:
    'A call to account. Hear what is asking to be integrated, then rise.',
  'The World':
    'A cycle completes. Take the view from the end before the next beginning.',
};

export function getTarotOfDay(date: Date): { card: string; description: string } {
  const key = date.toISOString().slice(0, 10);
  const h = hash32(key + 'tarot');
  const card = TAROT_MAJOR[h % TAROT_MAJOR.length];
  return { card, description: TAROT_DESCRIPTIONS[card] };
}

export const STORAGE_SIGN_KEY = 'earthmonitor-zodiac';
/** Legacy key — read once for migration if new key empty */
export const STORAGE_SIGN_KEY_LEGACY = 'softmonitor-zodiac';
